import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { ApiClientError } from '../../api/client'
import {
  createOrganization,
  getAdminOrgUsage,
  listOrganizations,
  type OrgEntry,
  setOrganizationActive,
} from '../../api/organizations'
import { useDialog } from '../../components/dialogs/useDialog'
import TokenRevealModal from '../../components/TokenRevealModal'
import type { AdminOrgUsageEntry } from '../../types/api'
import { formatFileSize } from '../../utils/format'

const inputClassName =
  'w-full px-4 py-3 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent'

export default function OrgListPage() {
  const dialog = useDialog()
  const [orgs, setOrgs] = useState<OrgEntry[]>([])
  const [usageData, setUsageData] = useState<Map<number, AdminOrgUsageEntry>>(new Map())
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [togglingOrgId, setTogglingOrgId] = useState<number | null>(null)
  const [revealedToken, setRevealedToken] = useState<string | null>(null)
  const [tokenModalTitle, setTokenModalTitle] = useState('New MCP API key')

  const [newSlug, setNewSlug] = useState('')
  const [newName, setNewName] = useState('')
  const [newWorkspaceDomain, setNewWorkspaceDomain] = useState('')
  const [newAdminEmail, setNewAdminEmail] = useState('')
  const [newAdminUsername, setNewAdminUsername] = useState('')
  const [newAdminPassword, setNewAdminPassword] = useState('')

  const loadOrgs = useCallback(async () => {
    try {
      const [orgList, usage] = await Promise.all([
        listOrganizations(),
        getAdminOrgUsage().catch(() => []),
      ])
      setOrgs(orgList)
      const usageMap = new Map<number, AdminOrgUsageEntry>()
      for (const u of usage) usageMap.set(u.orgId, u)
      setUsageData(usageMap)
    } catch {
      toast.error('Failed to load organizations')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadOrgs()
  }, [loadOrgs])

  const resetCreateForm = () => {
    setNewSlug('')
    setNewName('')
    setNewWorkspaceDomain('')
    setNewAdminEmail('')
    setNewAdminUsername('')
    setNewAdminPassword('')
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsCreating(true)
    try {
      const withAdmin = newAdminEmail.trim().length > 0 || newAdminUsername.trim().length > 0
      const created = await createOrganization({
        slug: newSlug.trim(),
        name: newName.trim(),
        ...(newWorkspaceDomain.trim().length > 0 && {
          workspaceDomain: newWorkspaceDomain.trim(),
        }),
        ...(withAdmin && {
          admin: {
            email: newAdminEmail.trim(),
            username: newAdminUsername.trim(),
            // Blank means passwordless: they sign in with any provider. No
            // password has to be invented and sent out of band.
            ...(newAdminPassword.length > 0 && { password: newAdminPassword }),
          },
        }),
      })
      toast.success(`Organization "${created.org.slug}" created`)
      setShowCreateForm(false)
      resetCreateForm()
      if (created.mcpKey && created.adminUser) {
        setTokenModalTitle(`MCP key for ${created.adminUser.username}`)
        setRevealedToken(created.mcpKey.token)
      }
      await loadOrgs()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create organization')
    } finally {
      setIsCreating(false)
    }
  }

  const handleToggleActive = async (org: OrgEntry) => {
    const action = org.isActive ? 'Deactivate' : 'Reactivate'
    const confirmed = await dialog.confirm({
      title: `${action} ${org.name}?`,
      description: org.isActive
        ? `Deactivating this org also deactivates every user in it. They will not be able to sign in until the org is reactivated.`
        : `Reactivating this org reactivates every user in it.`,
      confirmLabel: action,
      tone: org.isActive ? 'danger' : 'default',
    })
    if (!confirmed) return

    setTogglingOrgId(org.id)
    try {
      const updated = await setOrganizationActive(org.id, !org.isActive)
      setOrgs((prev) => prev.map((o) => (o.id === updated.id ? updated : o)))
      toast.success(
        `Organization "${org.slug}" ${updated.isActive ? 'reactivated' : 'deactivated'}`
      )
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 400) {
        toast.error(error.message)
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to update organization')
      }
    } finally {
      setTogglingOrgId(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-500" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-harbor-text">Organizations</h1>
          <p className="text-harbor-text-secondary mt-1">
            Manage organizations across the platform.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateForm((v) => !v)}
          className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
        >
          {showCreateForm ? 'Cancel' : 'Add organization'}
        </button>
      </div>

      {showCreateForm && (
        <form
          onSubmit={handleCreate}
          className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-4"
        >
          <h2 className="text-lg font-semibold text-harbor-text">New organization</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="org-slug"
                className="block text-sm font-medium text-harbor-text-secondary mb-1"
              >
                Slug
              </label>
              <input
                id="org-slug"
                type="text"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                required
                placeholder="acme"
                className={inputClassName}
              />
            </div>
            <div>
              <label
                htmlFor="org-name"
                className="block text-sm font-medium text-harbor-text-secondary mb-1"
              >
                Name
              </label>
              <input
                id="org-name"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                required
                placeholder="Acme Corp"
                className={inputClassName}
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="org-workspace-domain"
              className="block text-sm font-medium text-harbor-text-secondary mb-1"
            >
              Email domain <span className="text-harbor-text-muted">(optional)</span>
            </label>
            <input
              id="org-workspace-domain"
              type="text"
              value={newWorkspaceDomain}
              onChange={(e) => setNewWorkspaceDomain(e.target.value)}
              placeholder="acme.com"
              className={inputClassName}
            />
            <p className="mt-1 text-xs text-harbor-text-muted">
              Optional label for the org's email domain. Any user may be granted access regardless
              of domain — access is controlled by admin approval, not by domain match.
            </p>
          </div>

          <h3 className="text-base font-semibold text-harbor-text pt-2">
            Initial admin <span className="text-harbor-text-muted font-normal">(optional)</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="org-admin-email"
                className="block text-sm font-medium text-harbor-text-secondary mb-1"
              >
                Admin email
              </label>
              <input
                id="org-admin-email"
                type="email"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                placeholder="admin@acme.com"
                className={inputClassName}
              />
            </div>
            <div>
              <label
                htmlFor="org-admin-username"
                className="block text-sm font-medium text-harbor-text-secondary mb-1"
              >
                Admin username
              </label>
              <input
                id="org-admin-username"
                type="text"
                value={newAdminUsername}
                onChange={(e) => setNewAdminUsername(e.target.value)}
                placeholder="acme-admin"
                className={inputClassName}
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="org-admin-password"
              className="block text-sm font-medium text-harbor-text-secondary mb-1"
            >
              Admin password (optional)
            </label>
            <input
              id="org-admin-password"
              type="password"
              value={newAdminPassword}
              onChange={(e) => setNewAdminPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              className={inputClassName}
            />
            <p className="mt-1 text-xs text-harbor-text-muted">
              Leave blank for a passwordless admin. Either way they can sign in with Google, GitHub,
              or Microsoft — they choose at sign-in.
            </p>
          </div>

          <button
            type="submit"
            disabled={isCreating}
            className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isCreating ? 'Creating...' : 'Create organization'}
          </button>
        </form>
      )}

      <div className="rounded-xl border border-harbor-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-harbor-border">
            <thead className="bg-harbor-surface/80">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-harbor-text-secondary uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-harbor-text-secondary uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-harbor-text-secondary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-harbor-text-secondary uppercase tracking-wider">
                  Storage
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-harbor-text-secondary uppercase tracking-wider">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-harbor-text-secondary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-harbor-border bg-harbor-surface/40">
              {orgs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-harbor-text-muted">
                    No organizations found.
                  </td>
                </tr>
              ) : (
                orgs.map((org) => {
                  const usage = usageData.get(org.id)
                  const usedBytes = usage?.totalBytes ?? '0'
                  const limitBytes = usage?.effectiveLimitBytes ?? '0'
                  const pct =
                    limitBytes !== '0'
                      ? Math.min(100, Number((BigInt(usedBytes) * 100n) / BigInt(limitBytes)))
                      : 0
                  return (
                    <tr key={org.id}>
                      <td className="px-4 py-4">
                        <span className="font-mono text-sm text-harbor-text">{org.slug}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-harbor-text">{org.name}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                            org.isActive
                              ? 'bg-emerald-900/40 text-emerald-300'
                              : 'bg-red-900/30 text-red-300'
                          }`}
                        >
                          {org.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {usage ? (
                          <div className="min-w-[120px]">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-harbor-text-secondary">
                                {formatFileSize(usedBytes)}
                              </span>
                              <span className="text-harbor-text-muted">
                                / {formatFileSize(limitBytes)}
                              </span>
                            </div>
                            <div className="w-full bg-harbor-border rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${usage.overLimit ? 'bg-red-500' : 'bg-emerald-500'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-harbor-text-muted text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-harbor-text-secondary">
                        {new Date(org.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4">
                        <button
                          type="button"
                          disabled={togglingOrgId === org.id}
                          onClick={() => void handleToggleActive(org)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 ${
                            org.isActive
                              ? 'border-red-700/50 text-red-300 hover:bg-red-900/30'
                              : 'border-emerald-700/50 text-emerald-300 hover:bg-emerald-900/30'
                          }`}
                        >
                          {togglingOrgId === org.id
                            ? 'Working...'
                            : org.isActive
                              ? 'Deactivate'
                              : 'Reactivate'}
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <TokenRevealModal
        isOpen={revealedToken !== null}
        onClose={() => setRevealedToken(null)}
        title={tokenModalTitle}
        token={revealedToken}
      />
    </div>
  )
}
